/**
 * Declarative WebMCP helpers.
 * @see https://developer.chrome.com/docs/ai/webmcp/declarative-api
 */

import type { FormEvent } from "react";

export type LeadFormFieldValues = {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
};

/** camelCase сегмент для toolname (discuss-pilot → DiscussPilot). */
function toPascalCase(key: string): string {
  return key
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("");
}

/** Pass-through для native input/textarea → toolparamdescription. */
export function webmcpParam(description: string): {
  toolparamdescription: string;
} {
  return { toolparamdescription: description };
}

export const WEBMCP_PARAMS = {
  accountEmail: "Email аккаунта Идеалитика",
  accountPassword: "Пароль аккаунта",
  accountPasswordConfirm: "Повтор пароля (должен совпадать с паролем)",
  accountName: "Имя пользователя (как к вам обращаться)",
  newPassword: "Новый пароль (минимум 8 символов)",
  rememberSession:
    "Запомнить сессию на этом устройстве (значение true или false)",
  twoFactorCode: "6-значный код подтверждения входа из email",
  resetToken: "Токен сброса пароля из ссылки в письме (подставляется автоматически)",
  consentCookies: "Согласие на cookies (true или false)",
  consentPrivacy:
    "Согласие на обработку персональных данных (true или false, обязательно)",
  consentNewsletter:
    "Согласие на рассылку (true или false, необязательно)",
  planSlug: "Идентификатор тарифа (подставляется автоматически)",
  planName: "Название тарифа (подставляется автоматически)",
} as const;

export function leadToolName(formCaseKey: string): string {
  return `leadForm${toPascalCase(formCaseKey)}`;
}

export function leadToolDescription(title: string): string {
  return `Отправить заявку «${title}» в Идеалитика: контакты и комментарий попадут в CRM.`;
}

export function planInquiryToolName(planSlug: string): string {
  return `planInquiry${toPascalCase(planSlug)}`;
}

export function planInquiryToolDescription(planName: string): string {
  return `Отправить заявку на тариф «${planName}» в Идеалитика: контакты и задача попадут в CRM.`;
}

type AgentSubmitEvent = SubmitEvent & {
  agentInvoked?: boolean;
  respondWith?: (result: Promise<unknown>) => void;
};

export function isAgentInvokedSubmit(e: FormEvent): boolean {
  return Boolean((e.nativeEvent as AgentSubmitEvent).agentInvoked);
}

export function respondToAgentSubmit(
  e: FormEvent,
  result: Promise<unknown>,
): void {
  (e.nativeEvent as AgentSubmitEvent).respondWith?.(result);
}

/** FormData — корректно при заполнении агентом controlled-полей. */
export function readLeadFormValues(
  form: HTMLFormElement,
): LeadFormFieldValues {
  const fd = new FormData(form);
  const get = (key: string) => String(fd.get(key) ?? "").trim();
  return {
    name: get("name"),
    email: get("email"),
    phone: get("phone"),
    company: get("company"),
    message: get("message"),
  };
}

export function readPlanInquiryFormValues(form: HTMLFormElement): {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
  planSlug: string;
  planName: string;
} {
  const fd = new FormData(form);
  const get = (key: string) => String(fd.get(key) ?? "").trim();
  return {
    name: get("name"),
    email: get("email"),
    phone: get("phone"),
    company: get("company"),
    message: get("message"),
    planSlug: get("planSlug"),
    planName: get("planName"),
  };
}
